import {
  IconArrowLeft,
  IconArrowsRight,
  IconEmoji2,
  IconMuseSolid,
  IconWechat,
  IconWechatSolid,
} from '@/components/icons'
import { classNames } from '@/lib/classNames'
import { CheckIcon } from '@heroicons/react/24/outline'
import axios, { AxiosResponse } from 'axios'
import { UserProfile } from 'hooks/useUserProfile'
import { signIn } from 'next-auth/react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR, { KeyedMutator } from 'swr'
import { HomeViewType } from 'types/home'
import { QrcodeData, QrcodeStatus } from 'types/weixin'
import { useHomeViewType } from './MainContent'

export interface HomePageProps {
  userProfile?: UserProfile
  onGetUserProfile?: KeyedMutator<AxiosResponse<UserProfile, any>>
  needLogin?: boolean
}

enum Step {
  Login,
  Bind,
}

const steps = [
  {
    name: '登录 Muse',
    value: Step.Login,
    index: '1',
  },
  {
    name: '绑定/解绑微信号',
    value: Step.Bind,
    index: '2',
  },
]

export default function WechatContent({ userProfile, onGetUserProfile, needLogin }: HomePageProps) {
  const [_, setHomeViewType] = useHomeViewType()

  const needBindWeixin = useMemo(() => !userProfile?.userWeixin?.openId, [userProfile?.userWeixin?.openId])

  const [step, setStep] = useState(Step.Login)
  const [createdSceneStr, setCreatedSceneStr] = useState<string>()

  const [qrcodeData, setQrcodeData] = useState<QrcodeData>()

  const qrcodeSrc = useMemo(() => {
    return qrcodeData?.ticket
      ? `https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${encodeURIComponent(qrcodeData.ticket)}`
      : undefined
  }, [qrcodeData?.ticket])

  const handleClickBindWechat = useCallback(async () => {
    const { data } = await axios.get<QrcodeData>('/api/weixin/generateQrcode')
    setCreatedSceneStr(data.sceneStr)
    setQrcodeData(data)
  }, [])

  const { data: statusData, error } = useSWR<{ data: QrcodeData }>(
    createdSceneStr ? ['weixin.bind.getStatus', createdSceneStr] : undefined,
    () =>
      axios.get('/api/weixin/getQrcodeStatus', {
        params: {
          sceneStr: qrcodeData?.sceneStr,
        },
      }),
    {
      refreshInterval: needBindWeixin && qrcodeData && qrcodeData.status !== QrcodeStatus.EXPIRED ? 3000 : 0,
    },
  )

  useEffect(() => {
    setQrcodeData(statusData?.data)
    if (statusData?.data.status === QrcodeStatus.SUCCESS) {
      onGetUserProfile?.()
    }
  }, [onGetUserProfile, statusData])

  const unbindWeixin = useCallback(async () => {
    await axios.post('/api/weixin/unbind')
    onGetUserProfile?.()
  }, [onGetUserProfile])

  useEffect(() => {
    if (error) console.log('@@@ error', error)
  }, [error])

  return (
    <>
      <div className="relative flex flex-col md:min-h-screen justify-center p-0">
        <div className="sm:mx-auto sm:w-full flex flex-col justify-center items-center pt-[30px] md:pt-10 px-5 sm:px-10 mb-[40px] md:mb-[30px]">
          <div className="flex justify-center space-x-4 items-center mb-3">
            <IconWechatSolid className="w-[46px] h-[46px]" />
            <IconArrowsRight className="w-[28px] h-[15px]" />
            <IconMuseSolid className="w-[50px] h-[50px]" />
          </div>
          <h2 className="text-center text-[32px] font-normal tracking-tight">微信公众号助手</h2>
        </div>

        {/* steps */}
        <ol role="list" className="hidden md:flex justify-center items-center mb-[60px]">
          {steps.map((stepItem, stepIdx) => (
            <li
              key={stepItem.value}
              className={classNames(stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-[160px]' : '', 'relative')}
            >
              {stepItem.value < step ? (
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <button
                      className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full bg-accent-600 hover:bg-indigo-900"
                      onClick={() => setStep(stepItem.value)}
                    >
                      <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                      <span className="sr-only">{stepItem.name}</span>
                    </button>
                    <div className="w-[38px] relative text-basic-800 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              ) : stepItem.value === step ? (
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <span
                      className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-accent-600 bg-white"
                      aria-current="step"
                    >
                      <span className="text-accent-600 text-lg leading-5" aria-hidden="true">
                        {stepItem.index}
                      </span>
                      <span className="sr-only">{stepItem.name}</span>
                    </span>
                    <div className="w-[38px] relative text-basic-800 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <span className="group relative flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-basic-400 bg-white">
                      <span className="text-basic-500 text-lg leading-5" aria-hidden="true">
                        {stepItem.index}
                      </span>
                      <span className="sr-only">{stepItem.name}</span>
                    </span>
                    <div className="w-[38px] relative text-basic-500 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ol>

        <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-4xl px-5 mb-24 flex justify-center">
          <div
            className={classNames(
              `bg-white px-4 py-6 rounded-xl shadow sm:rounded-lg transition-shadow relative overflow-hidden w-[312px] mx-auto hover:shadow-xl`,
              'flex flex-col justify-between items-center space-y-[40px]',
            )}
          >
            {step !== Step.Bind ? (
              needLogin ? (
                <>
                  <div className="text-center text-sm text-black">需要先登录噢~</div>
                  <span className="block text-[54px] leading-[54px]">🤣</span>
                  <div className="w-full">
                    <button
                      className="bg-accent-600 w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        signIn('muse')
                      }}
                    >
                      去登录 Muse
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center text-sm text-black">真棒！</div>
                  <IconEmoji2 className="w-[54px] h-[54px]" />
                  {/* <span className="block text-[54px] leading-[54px]">😊</span> */}
                  <div className="w-full">
                    <button
                      className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        setStep(Step.Bind)
                        handleClickBindWechat()
                      }}
                    >
                      已登录，下一步
                    </button>
                  </div>
                </>
              )
            ) : needBindWeixin ? (
              <>
                <div className="text-center text-sm text-black">最后一步啦👇🥳~</div>
                <div className="mx-auto flex flex-col items-center relative w-[120px] h-[120px] rounded overflow-hidden">
                  {qrcodeSrc && (
                    <>
                      <Image
                        src={qrcodeSrc}
                        alt="bind-wechat"
                        className="w-[120px] h-[120px] bg-white"
                        width={300}
                        height={300}
                      />
                      {qrcodeData?.status === QrcodeStatus.EXPIRED ? (
                        <div className="absolute inset-0 bg-black/70 flex flex-col justify-center items-center space-y-2 text-white">
                          <span className="text-xs">二维码已过期</span>
                          <button
                            className="px-2 py-1 text-xs bg-sky-500 rounded-sm"
                            onClick={() => {
                              handleClickBindWechat()
                            }}
                          >
                            刷新
                          </button>
                        </div>
                      ) : qrcodeData?.status === QrcodeStatus.SCANNED ? (
                        <div className="absolute inset-0 bg-black/70 flex flex-col justify-center items-center space-y-2 text-white">
                          <span className="text-xs">已扫描</span>
                        </div>
                      ) : qrcodeData?.status === QrcodeStatus.ERROR ? (
                        <div className="absolute inset-0 bg-black/70 flex flex-col justify-center items-center space-y-2 text-white">
                          <span className="text-xs">绑定出错</span>
                        </div>
                      ) : qrcodeData?.status === QrcodeStatus.SUCCESS ? (
                        <div className="absolute inset-0 bg-black/70 flex flex-col justify-center items-center space-y-2 text-white">
                          <span className="text-xs">绑定成功</span>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                <div className="w-full">
                  <button
                    className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                    onClick={() => {
                      handleClickBindWechat()
                    }}
                  >
                    <IconWechat className="w-[18px] h-[14px]" />
                    <span>{'扫码绑定微信号'}</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center text-sm text-black">解绑账号</div>
                <span className="text-[54px] leading-[54px]">👐</span>
                <div className="w-full">
                  <button
                    className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                    onClick={() => {
                      unbindWeixin()
                    }}
                  >
                    <IconWechat className="w-[18px] h-[14px]" />
                    <span>解绑微信公众号</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="fixed top-[20px] left-0">
        <button
          className="h-[38px] bg-white text-foreground inline-flex space-x-1 items-center rounded-r-xl shadow-md text-sm py-1.5 px-2 leading-[26px] transition-colors hover:text-basic-600"
          onClick={() => {
            setHomeViewType(HomeViewType.Default)
          }}
        >
          <IconArrowLeft className="w-4 h-4" />
          <span>返回</span>
        </button>
      </div>
    </>
  )
}
